"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { SORT_OPTIONS, type FilterState } from "@/components/ui/search-filters"
import type { FilterRailState } from "@/components/ui/tls/filter-rail"

const isDev = process.env.NODE_ENV === 'development'

// Default filter state
const DEFAULT_FILTERS: FilterState = {
  query: '',
  category: 'All',
  model: '',
  sortBy: 'downloads',
  sortOrder: 'desc'
}

/**
 * Parse a combined ?sort query param (e.g. "created_at_desc") into its
 * component sortBy + sortOrder values. Falls back to the download default
 * when the value is unrecognized or absent.
 */
export function parseSortParam(raw: string | null): Pick<FilterState, 'sortBy' | 'sortOrder'> {
  const match = SORT_OPTIONS.find((opt) => opt.value === raw)
  if (match) return { sortBy: match.sortBy, sortOrder: match.sortOrder }
  // Legacy bare-column values like "downloads" or "created_at" (no order suffix)
  const legacyMatch = SORT_OPTIONS.find((opt) => opt.sortBy === raw && opt.sortOrder === 'desc')
  if (legacyMatch) return { sortBy: legacyMatch.sortBy, sortOrder: legacyMatch.sortOrder }
  return { sortBy: 'downloads', sortOrder: 'desc' }
}

// Helper function to add debug info
const addDebugInfo = (message: string) => {
  if (!isDev) return
  const timestamp = new Date().toLocaleTimeString()
  const debugMessage = `[${timestamp}] [SoundsPage] ${message}`
  console.log(debugMessage)
}

/**
 * Owns the /sounds filter state and the bridge between the two filter
 * surfaces — the FilterRail (desktop aside / mobile sheet) and the
 * SearchFilters bar — keeping category + model in sync in both directions.
 * `filters.model` / `railFilters.model` always hold the canonical full Tesla
 * model name ("Model S", "Model 3", ..., "Cybertruck") — the same vocabulary
 * the query layer (`compatibleModels`) and the SearchFilters bar use. The
 * rail's compact code buttons (S/3/X/Y/CT) translate to that name at the
 * component boundary (see lib/model-data.ts modelCodeToName) before calling
 * onChange, so this hook never has to know about codes.
 *
 * URL is read through the injected `get` so the hook stays testable.
 * `syncRailToUrl`, when provided, is called with the merged rail state after
 * every rail change so the caller can push `?category=&model=&picks=1` the
 * same way SearchFilters.updateURL does (injected rather than called via
 * useRouter directly, so this hook stays router-free and unit-testable).
 * `filterSheetOpen` deliberately stays in the parent (pure UI state).
 */
export function useFilterSync(
  get: (key: string) => string | null,
  syncRailToUrl?: (next: FilterRailState) => void,
) {
  // Initialize selectedCategory from URL params
  const urlCategory = get('category')
  const [selectedCategory, setSelectedCategory] = useState<string>(urlCategory || 'All')

  // Filter rail state (desktop sticky aside + mobile sheet)
  const [railFilters, setRailFilters] = useState<FilterRailState>({
    category: urlCategory || 'All',
    model: get('model') || '',
    picksOnly: get('picks') === '1',
  })

  // Store current filters in state (single source of truth)
  const urlFilters: FilterState = {
    ...DEFAULT_FILTERS,
    query: get('q') || get('search') || '',
    category: urlCategory || 'All',
    model: get('model') || '',
    ...parseSortParam(get('sort')),
  }
  const urlRailFilters: FilterRailState = {
    category: urlCategory || 'All',
    model: get('model') || '',
    picksOnly: get('picks') === '1',
  }
  const urlFilterKey = JSON.stringify({ ...urlFilters, picksOnly: urlRailFilters.picksOnly })
  const lastSyncedUrlFilterKeyRef = useRef(urlFilterKey)
  const [currentFilters, setCurrentFilters] = useState<FilterState>(urlFilters)

  // Latest rail state, mirrored so both writers below can merge against it
  // synchronously without reaching for a `setRailFilters` updater.
  //
  // TES-1873: `handleRailChange` used to do the merge *inside* a
  // `setRailFilters(prev => …)` updater and fire its side effects — the two
  // sibling setStates and `syncRailToUrl` (a `router.push`) — from in there.
  // React runs updaters during the render phase, so those side effects landed
  // mid-render: the browser reported `Cannot update a component (Router) while
  // rendering a different component (SoundsPageClient)`, with the updater
  // frame sitting under `useState` inside `useFilterSync`. A render-phase
  // update is also the only path on which React switches to its re-render
  // dispatcher, which is the sole thrower of "Rendered more hooks than during
  // the previous render." Beyond that, React is free to run an updater more
  // than once (StrictMode double-invokes them precisely to surface this), so
  // one rail change pushed two history entries.
  //
  // Everything below therefore keeps updaters pure and does side effects in
  // the event-handler scope, where they belong.
  const railFiltersRef = useRef(railFilters)

  const applyRailFilters = useCallback((merged: FilterRailState) => {
    railFiltersRef.current = merged
    setRailFilters(merged)
  }, [])

  // URL navigation restores the whole filter state, including absent params
  // as their defaults. This keeps the result list and both filter surfaces in
  // sync when the browser moves through history.
  useEffect(() => {
    if (lastSyncedUrlFilterKeyRef.current === urlFilterKey) return
    lastSyncedUrlFilterKeyRef.current = urlFilterKey

    const nextCategory = get('category') || 'All'
    const nextFilters: FilterState = {
      ...DEFAULT_FILTERS,
      query: get('q') || get('search') || '',
      category: nextCategory,
      model: get('model') || '',
      ...parseSortParam(get('sort')),
    }
    setCurrentFilters(nextFilters)
    setSelectedCategory(nextCategory)
    applyRailFilters({
      category: nextCategory,
      model: nextFilters.model,
      picksOnly: get('picks') === '1',
    })
  }, [get, urlFilterKey, applyRailFilters])

  // Handle search callback - just updates the filter state
  const handleSearch = useCallback((filters: FilterState) => {
    addDebugInfo(`Updating filters: ${JSON.stringify(filters)}`)
    setCurrentFilters(filters)
    // Also update selected category for UI sync
    setSelectedCategory(filters.category)
    // Keep rail in sync with external filter changes (e.g. SearchFilters pill)
    applyRailFilters({
      ...railFiltersRef.current,
      category: filters.category,
      model: filters.model || '',
    })
  }, [applyRailFilters])

  // Handle rail filter changes — bridge into the existing filter system
  const handleRailChange = useCallback((next: Partial<FilterRailState>) => {
    const merged = { ...railFiltersRef.current, ...next }
    applyRailFilters(merged)
    // Sync category + model into SearchFilters state
    setCurrentFilters(cf => ({
      ...cf,
      category: merged.category,
      model: merged.model,
    }))
    setSelectedCategory(merged.category)
    syncRailToUrl?.(merged)
  }, [applyRailFilters, syncRailToUrl])

  return {
    selectedCategory,
    setSelectedCategory,
    railFilters,
    currentFilters,
    handleSearch,
    handleRailChange,
  }
}
